import React from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Fire, Lightning, Star } from "@phosphor-icons/react";
import {
  clearTryNowJourney,
  fetchApplicantProfile,
  fetchHapTable,
  fetchReferenceDataReservations,
  fetchResultPreview,
  getApplicantFromStorage,
  getTryNowJourney,
  isPremiumActive,
  getReferenceDataFromStorage,
  type HapTableRequest,
  type HapTableResponse,
  type ResultPreviewResponse,
  useAuthState,
  useSubscriptionState,
} from "../../../hallticket-lib";
import { PRO_PLANS_ENABLED } from "../../../config/featureFlags";
import { HapHeatmapPanel, type ResultReviewSort } from "./HapHeatmapPanel";
import { ShortlistedColleges } from "./ShortlistedColleges";

const DEFAULT_HAP_REQUEST: HapTableRequest = {
  percentile: 97.32,
  category: "",
};

const RESULT_REVIEW_FILTERS_STORAGE_KEY = "hallticket.resultsReview.filters.v1";

type ExamValue = string;
type Option = { label: string; value: string };

type ResultReviewTab = "picks" | "shortlisted-colleges";
const ALL_LOCATIONS_VALUE = "__all_locations__";

interface ResultsReviewProps {
  onUpgradeToPro?: () => void;
}

function canUseLocalStorage(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function normalizeText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function resolveOptionValue(value: string, options: Option[]): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  if (options.length === 0) {
    return trimmed;
  }

  const normalizedInput = normalizeText(trimmed);
  const match = options.find(
    (option) => normalizeText(option.value) === normalizedInput || normalizeText(option.label) === normalizedInput,
  );
  return match?.value ?? trimmed;
}

function resolveExamFromTryNow(exam: string, examOptions: Option[]): string | null {
  if (examOptions.length === 0) {
    return null;
  }
  const resolved = resolveOptionValue(exam, examOptions);
  return resolved || examOptions[0].value;
}

function resolveCategoryFromTryNow(category: string, categoryOptions: Option[]): string {
  return resolveOptionValue(category, categoryOptions);
}

function getSavedResultReviewFilters(): { exam: string; percentile: number; category: string } | null {
  if (!canUseLocalStorage()) {
    return null;
  }

  const raw = window.localStorage.getItem(RESULT_REVIEW_FILTERS_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as {
      exam?: unknown;
      percentile?: unknown;
      category?: unknown;
    };

    const exam = typeof parsed.exam === "string" ? parsed.exam : "";
    const category = typeof parsed.category === "string" ? parsed.category : "";
    const percentile =
      typeof parsed.percentile === "number" && Number.isFinite(parsed.percentile)
        ? parsed.percentile > 0 && parsed.percentile <= 100
          ? parsed.percentile
          : DEFAULT_HAP_REQUEST.percentile
        : DEFAULT_HAP_REQUEST.percentile;

    return { exam, percentile, category };
  } catch {
    return null;
  }
}

export function ResultsReview({ onUpgradeToPro }: ResultsReviewProps) {
  const { applicant } = useAuthState();
  const subscriptionState = useSubscriptionState();
  const premiumEnabled = PRO_PLANS_ENABLED && isPremiumActive(subscriptionState.snapshot);
  const isReadyFreeUser = PRO_PLANS_ENABLED && subscriptionState.status === "ready" && !premiumEnabled;
  const savedFilters = React.useMemo(() => getSavedResultReviewFilters(), []);
  const tryNowJourney = React.useMemo(() => getTryNowJourney(), []);
  const referenceData = React.useMemo(() => getReferenceDataFromStorage(), []);
  const [categoryOptions, setCategoryOptions] = React.useState<Option[]>([]);
  const examOptions = React.useMemo(() => {
    const exams = referenceData?.exams?.length ? referenceData.exams : [];
    return exams.map((exam) => ({ label: exam, value: exam }));
  }, [referenceData]);

  React.useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const reservations = await fetchReferenceDataReservations();
        if (!isMounted) {
          return;
        }
        const vertical = reservations.filter((item) => item.type === "Vertical");
        setCategoryOptions(vertical.map((item) => ({ label: item.name, value: item.name })));
      } catch {
        if (!isMounted) {
          return;
        }
        setCategoryOptions([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const [resultReviewExam, setResultReviewExam] = React.useState<ExamValue>(() => savedFilters?.exam ?? "");
  const [percentileInputValue, setPercentileInputValue] = React.useState<string>(() => String(tryNowJourney?.percentile ?? savedFilters?.percentile ?? DEFAULT_HAP_REQUEST.percentile));
  const [resultReviewPayload, setResultReviewPayload] = React.useState<HapTableRequest>(() => ({
    ...DEFAULT_HAP_REQUEST,
    percentile: tryNowJourney?.percentile ?? savedFilters?.percentile ?? DEFAULT_HAP_REQUEST.percentile,
    category: tryNowJourney?.categories?.[0] ?? tryNowJourney?.category ?? savedFilters?.category ?? DEFAULT_HAP_REQUEST.category,
    exam: savedFilters?.exam?.trim() ? savedFilters.exam : undefined,
  }));
  const [, setResultReviewAnalyzedPayload] = React.useState<HapTableRequest>(DEFAULT_HAP_REQUEST);
  const [resultReviewStatus, setResultReviewStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
  const [resultReviewData, setResultReviewData] = React.useState<HapTableResponse | null>(null);
  const [, setResultPreviewSnapshot] = React.useState<ResultPreviewResponse | null>(null);
  const [resultReviewError, setResultReviewError] = React.useState<string>("");
  const [resultReviewTab, setResultReviewTab] = React.useState<ResultReviewTab>("picks");
  const [resultReviewSort, setResultReviewSort] = React.useState<ResultReviewSort>("avg");
  const [resultReviewCourseFilter, setResultReviewCourseFilter] = React.useState<string[]>([]);
  const [resultReviewLocationFilter, setResultReviewLocationFilter] = React.useState<string>(ALL_LOCATIONS_VALUE);
  const [hasBlockedDecimalAttempt, setHasBlockedDecimalAttempt] = React.useState(false);
  const lastSyncedProfileCategory = React.useRef("");
  const hasTriggeredInitialLookup = React.useRef(false);
  const hasAppliedTryNowJourney = React.useRef(false);

  const shortlistRequest = resultReviewPayload;
  const showDecimalPremiumMessage = isReadyFreeUser && hasBlockedDecimalAttempt;
  const roundedFreeUserPercentile = Math.trunc(Math.max(1, Math.min(100, resultReviewPayload.percentile)));

  const runResultReviewLookup = React.useCallback(async () => {
    if (!resultReviewPayload.exam?.trim()) {
      setResultReviewError("Please select your entrance exam to analyse results.");
      setResultReviewStatus("error");
      return;
    }
    if (!resultReviewPayload.category?.trim()) {
      setResultReviewError("Please select your category to analyse results.");
      setResultReviewStatus("error");
      return;
    }
    if (!Number.isFinite(resultReviewPayload.percentile) || resultReviewPayload.percentile <= 0 || resultReviewPayload.percentile > 100) {
      return;
    }

    setResultReviewStatus("loading");
    setResultReviewError("");

    const previewPromise = fetchResultPreview({
      percentile: resultReviewPayload.percentile,
      gender: (applicant?.gender ?? getApplicantFromStorage()?.gender ?? "").trim(),
      category: (resultReviewPayload.category ?? "").trim(),
      exam: (resultReviewPayload.exam ?? "").trim(),
    }).catch(() => null);

    try {
      const response = await fetchHapTable(resultReviewPayload);
      const preview = await previewPromise;
      setResultReviewData(response);
      setResultPreviewSnapshot(preview);
      setResultReviewAnalyzedPayload({ ...resultReviewPayload });
      const availableCourseNames = response.courses.map((course) => course.course_name);
      const availableCourseNameSet = new Set(availableCourseNames);
      setResultReviewCourseFilter((prev) =>
        prev.length === 0
          ? availableCourseNames
          : prev.filter((courseName) => availableCourseNameSet.has(courseName)),
      );
      setResultReviewLocationFilter(ALL_LOCATIONS_VALUE);
      setResultReviewStatus("success");
    } catch (error) {
      await previewPromise;
      const message = error instanceof Error && error.message
        ? error.message
        : "Failed to fetch result review data.";
      setResultReviewError(message);
      setResultReviewData(null);
      setResultPreviewSnapshot(null);
      setResultReviewStatus("error");
    }
  }, [applicant?.gender, resultReviewPayload]);

  React.useEffect(() => {
    if (hasTriggeredInitialLookup.current) {
      return;
    }
    if (!resultReviewPayload.exam?.trim() || !resultReviewPayload.category?.trim()) {
      return;
    }

    hasTriggeredInitialLookup.current = true;
    void runResultReviewLookup();
  }, [resultReviewPayload.category, resultReviewPayload.exam, runResultReviewLookup]);

  React.useEffect(() => {
    if (!resultReviewExam && examOptions.length > 0) {
      const nextExam = examOptions[0].value;
      setResultReviewExam(nextExam);
      setResultReviewPayload((prev) => ({
        ...prev,
        exam: nextExam,
      }));
    }
  }, [examOptions, resultReviewExam]);

  React.useEffect(() => {
    if (!tryNowJourney) {
      return;
    }
    clearTryNowJourney();
  }, [tryNowJourney]);

  React.useEffect(() => {
    if (!tryNowJourney || hasAppliedTryNowJourney.current) {
      return;
    }
    if (examOptions.length === 0) {
      return;
    }

    const mappedExam = resolveExamFromTryNow(tryNowJourney.exam, examOptions);
    const categorySeed = tryNowJourney.categories?.[0] ?? tryNowJourney.category;
    const mappedCategory = resolveCategoryFromTryNow(categorySeed, categoryOptions);
    const nextPercentile = Math.max(0, Math.min(100, tryNowJourney.percentile));

    if (mappedExam) {
      setResultReviewExam(mappedExam);
    }
    setPercentileInputValue(String(nextPercentile));
    setResultReviewPayload((prev) => ({
      ...prev,
      exam: mappedExam ?? prev.exam,
      category: mappedCategory || prev.category,
      percentile: nextPercentile,
    }));
    setResultReviewStatus("idle");
    setResultReviewError("");
    hasAppliedTryNowJourney.current = true;
  }, [categoryOptions, examOptions, tryNowJourney]);

  React.useEffect(() => {
    setResultReviewPayload((prev) => ({
      ...prev,
      exam: resultReviewExam?.trim() ? resultReviewExam : undefined,
    }));
  }, [resultReviewExam]);

  React.useEffect(() => {
    if (!isReadyFreeUser) {
      return;
    }
    setResultReviewPayload((prev) => {
      const rounded = Math.trunc(Math.max(1, Math.min(100, prev.percentile)));
      if (prev.percentile === rounded) {
        return prev;
      }
      return { ...prev, percentile: rounded };
    });
  }, [isReadyFreeUser]);

  React.useEffect(() => {
    if (!isReadyFreeUser) {
      setHasBlockedDecimalAttempt(false);
    }
  }, [isReadyFreeUser]);

  React.useEffect(() => {
    if (!canUseLocalStorage()) {
      return;
    }

    window.localStorage.setItem(
      RESULT_REVIEW_FILTERS_STORAGE_KEY,
      JSON.stringify({
        exam: resultReviewExam ?? "",
        percentile: resultReviewPayload.percentile,
        category: resultReviewPayload.category ?? "",
      }),
    );
  }, [resultReviewExam, resultReviewPayload.category, resultReviewPayload.percentile]);

  React.useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const fromDb = await fetchApplicantProfile();
        if (!isMounted || !fromDb?.category?.trim()) {
          return;
        }
        setResultReviewPayload((prev) => {
          if (prev.category?.trim()) {
            return prev;
          }
          return { ...prev, category: fromDb.category!.trim() };
        });
      } catch {
        // Keep page resilient; fallback to auth/local value/default handled below.
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (categoryOptions.length === 0) {
      return;
    }

    const profileCategory = (
      applicant?.verticalReservation
      || applicant?.category
      || getApplicantFromStorage()?.verticalReservation
      || getApplicantFromStorage()?.category
      || ""
    ).trim();
    const categoryFromProfile = profileCategory
      ? categoryOptions.find((option) => option.value === profileCategory)?.value ?? profileCategory
      : "";

    if (!categoryFromProfile) {
      if (!resultReviewPayload.category?.trim()) {
        const nextCategory = categoryOptions[0].value;
        setResultReviewPayload((prev) => ({ ...prev, category: nextCategory }));
        setResultReviewStatus("idle");
        setResultReviewError("");
      }
      return;
    }

    if (categoryFromProfile === lastSyncedProfileCategory.current) {
      return;
    }

    lastSyncedProfileCategory.current = categoryFromProfile;
    setResultReviewPayload((prev) => ({ ...prev, category: categoryFromProfile }));
    setResultReviewStatus("idle");
    setResultReviewError("");
  }, [applicant?.category, applicant?.verticalReservation, categoryOptions, resultReviewPayload.category]);

  return (
    <section>
      <div className="rr-input-card">
        <div className="rr-input-row">
          <div className="rr-input-group">
            <label htmlFor="rr-exam">Exam</label>
            <Dropdown
              inputId="rr-exam"
              className="rr-input-control"
              panelClassName="rr-dropdown-panel"
              value={resultReviewExam || null}
              options={examOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setResultReviewExam(e.value as ExamValue)}
              placeholder="Select entrance exam"
            />
          </div>

          <div className="rr-input-group">
            <label htmlFor="rr-category">Category</label>
            <InputText
              id="rr-category"
              className="rr-input-control rr-input-control-static p-inputtext-sm"
              value={resultReviewPayload.category || "—"}
              readOnly
            />
          </div>

          <div className="rr-input-group rr-percentile-group">
            <label htmlFor="rr-percentile">Your Score</label>
            <InputText
              id="rr-percentile"
              className="rr-input-control rr-input-control-static p-inputtext-sm"
              inputMode={isReadyFreeUser ? "numeric" : "decimal"}
              value={percentileInputValue}
              onChange={(e) => {
                const rawValue = e.target.value;
                const allowDecimals = !isReadyFreeUser;
                const sanitized = allowDecimals
                  ? rawValue.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1")
                  : rawValue.replace(/[^\d]/g, "");

                if (!allowDecimals && rawValue.includes(".")) {
                  setHasBlockedDecimalAttempt(true);
                }

                if (sanitized === "") {
                  setPercentileInputValue("");
                  setResultReviewStatus("idle");
                  setResultReviewError("");
                  return;
                }

                const parsed = Number(sanitized);
                if (!Number.isFinite(parsed)) {
                  return;
                }

                if (allowDecimals) {
                  if (sanitized === "0") {
                    return;
                  }
                  if (parsed > 100) {
                    return;
                  }
                  if (parsed <= 0 && !/^0\.\d*$/.test(sanitized) && sanitized !== ".") {
                    return;
                  }
                } else {
                  if (parsed <= 0 || parsed > 100) {
                    return;
                  }
                }

                setPercentileInputValue(sanitized);
                setResultReviewPayload((prev) => ({
                  ...prev,
                  percentile: allowDecimals ? parsed : Math.trunc(parsed),
                }));
                setResultReviewStatus("idle");
                setResultReviewError("");
              }}
            />
          </div>

          <Button
            className="btn-rr-analyze"
            type="button"
            label={resultReviewStatus === "loading" ? "Analyzing..." : "Analyze →"}
            onClick={() => void runResultReviewLookup()}
          />
        </div>

        {PRO_PLANS_ENABLED && showDecimalPremiumMessage && (
          <div className="rr-pro-note" role="status" aria-live="polite">
            <div className="rr-pro-note__icon" aria-hidden>
              <Lightning size={12} weight="bold" />
            </div>
            <p className="rr-pro-note__text">
              <strong>Pro feature</strong> - Decimal precision (e.g. 95.5) requires a Pro plan. Your score has
              been rounded to <strong>{roundedFreeUserPercentile} percentile</strong> for analysis.{" "}
              <button type="button" className="rr-pro-note__link" onClick={onUpgradeToPro}>Upgrade to Pro -&gt;</button>
            </p>
          </div>
        )}
      </div>

      {resultReviewError && <p className="rr-error-msg">{resultReviewError}</p>}

      <div className="rr-tabs">
        <Button
          type="button"
          text
          className={`rr-tab-btn${resultReviewTab === "picks" ? " active" : ""}`}
          onClick={() => setResultReviewTab("picks")}
        >
          <Fire size={16} weight="regular" />
          HallTicket Picks
        </Button>
        <Button
          type="button"
          text
          className={`rr-tab-btn${resultReviewTab === "shortlisted-colleges" ? " active" : ""}`}
          onClick={() => setResultReviewTab("shortlisted-colleges")}
        >
          <Star size={16} weight="regular" />
          Your Shortlisted Colleges
        </Button>
      </div>

      {resultReviewTab === "picks" && resultReviewStatus === "loading" && !resultReviewData && (
        <div className="rr-loading-state">
          <ProgressSpinner style={{ width: "2.2rem", height: "2.2rem" }} strokeWidth="6" animationDuration=".9s" />
          <span className="rr-loading-text">Fetching latest data...</span>
        </div>
      )}

      {resultReviewData && resultReviewTab === "picks" && (
        <HapHeatmapPanel
          data={resultReviewData}
          status={resultReviewStatus}
          title=""
          subtitle=""
          loadingOverlayText="Refreshing picks..."
          sortMode={resultReviewSort}
          courseFilter={resultReviewCourseFilter}
          onSortModeChange={setResultReviewSort}
          onCourseFilterChange={setResultReviewCourseFilter}
          showLocationFilter
          locationFilter={resultReviewLocationFilter}
          onLocationFilterChange={setResultReviewLocationFilter}
        />
      )}

      {resultReviewTab === "shortlisted-colleges" && (
        <ShortlistedColleges
          request={{
            percentile: shortlistRequest.percentile,
            category: shortlistRequest.category,
            exam: shortlistRequest.exam,
          }}
        />
      )}

    </section>
  );
}
